import { address } from 'helpers';
import { Fade } from 'helpers/animation';
import useFormInput from 'hooks/form/useFormInput';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import {
  DESKTOP_MIN_WIDTH,
  REGISTER_ADDRESS_FINDER_NAME,
  REGISTER_ADDRESS_POST_CODE_NAME,
  REGISTER_ADDRESS_STATE_NAME,
  REGISTER_ADDRESS_STREET_NAME_NAME,
  REGISTER_ADDRESS_STREET_NO_NAME,
  REGISTER_ADDRESS_STREET_TYPE_NAME,
  REGISTER_ADDRESS_SUBURB_NAME,
  REGISTER_ADDRESS_UNITNAME_NAME,
} from '../../../../../constants';

const customFormInputStyleMobile = {
  [REGISTER_ADDRESS_UNITNAME_NAME]: {
    width: 'calc(50% - 4px)',
    float: 'left',
    marginRight: '4px',
    zIndex: 1,
  },
  [REGISTER_ADDRESS_STREET_NO_NAME]: {
    width: 'calc(50% - 4px)',
    float: 'left',
    marginLeft: '4px',
    zIndex: 1,
  },
  [REGISTER_ADDRESS_STREET_NAME_NAME]: {
    width: 'calc(50% - 4px)',
    float: 'left',
    marginRight: '4px',
    zIndex: 1,
  },
  [REGISTER_ADDRESS_SUBURB_NAME]: {
    width: 'calc(50% - 4px)',
    float: 'left',
    marginRight: '4px',
    zIndex: 1,
    marginBottom: '96px',
  },
  [REGISTER_ADDRESS_STATE_NAME]: {
    width: 'calc(50% - 4px)',
    float: 'left',
    marginLeft: '4px',
    zIndex: 1,
    marginBottom: '96px',
  },
};

const customFormInputStyle = {
  [REGISTER_ADDRESS_UNITNAME_NAME]: {
    width: 'calc(50% - 12px)',
    float: 'left',
    marginRight: '12px',
  },
  [REGISTER_ADDRESS_STREET_NO_NAME]: {
    width: 'calc(50% - 12px)',
    float: 'left',
    marginLeft: '12px',
  },
  [REGISTER_ADDRESS_STREET_NAME_NAME]: {
    width: 'calc(50% - 12px)',
    float: 'left',
    marginRight: '12px',
  },
  [REGISTER_ADDRESS_SUBURB_NAME]: {
    width: 'calc(50% - 12px)',
    float: 'left',
    marginRight: '12px',
    marginBottom: '96px',
  },
  [REGISTER_ADDRESS_STATE_NAME]: {
    width: 'calc(50% - 12px)',
    float: 'left',
    marginLeft: '12px',
    marginBottom: '96px',
  },
};

const FormInputValidation = ({
  field,
  information,
  setInformation,
  validation,
  setValidation,
  checkFormInputValidation,
}) => {
  const { t } = useLang();
  const { isDesktop } = useDetectDevice();

  const {
    inputRef,
    resetFormInput,
    setValidationFormInput,
    getValueTrim,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const isTextColorDisabled = [
    REGISTER_ADDRESS_SUBURB_NAME,
    REGISTER_ADDRESS_STATE_NAME,
  ].includes(field.name);

  const handleOnChange = (e) => {
    const { value, name } = e.target;
    let valueTrim = getValueTrim(value);

    if (!valueTrim) {
      resetFormInput({
        information,
        setInformation,
        validation,
        setValidation,
      });
      return;
    }

    let newInformation = {
      ...information,
      [name]: valueTrim,
    };

    newInformation = {
      ...newInformation,
      [REGISTER_ADDRESS_UNITNAME_NAME]:
        newInformation[REGISTER_ADDRESS_UNITNAME_NAME],
      [REGISTER_ADDRESS_STREET_NO_NAME]:
        newInformation[REGISTER_ADDRESS_STREET_NO_NAME],
      [REGISTER_ADDRESS_STREET_NAME_NAME]:
        newInformation[REGISTER_ADDRESS_STREET_NAME_NAME],
    };

    newInformation = {
      ...newInformation,
      [REGISTER_ADDRESS_FINDER_NAME]: address.addressCombine(
        newInformation[REGISTER_ADDRESS_UNITNAME_NAME],
        newInformation[REGISTER_ADDRESS_STREET_NO_NAME],
        newInformation[REGISTER_ADDRESS_STREET_NAME_NAME],
        newInformation[REGISTER_ADDRESS_STREET_TYPE_NAME]?.key,
        newInformation[REGISTER_ADDRESS_SUBURB_NAME],
        newInformation[REGISTER_ADDRESS_STATE_NAME],
        newInformation[REGISTER_ADDRESS_POST_CODE_NAME]
      ),
    };

    setInformation(newInformation);

    setValidationFormInput({
      validation,
      setValidation,
      checkFormInputValidation: () =>
        checkFormInputValidation(newInformation[name], field, validation),
    });
  };

  const handleOnBlur = (e) => {
    handleOnChange(e);
  };

  return (
    <FormGroupStyled
      style={
        !isDesktop
          ? customFormInputStyleMobile[field.name]
          : customFormInputStyle[field.name]
      }
    >
      <FormLabel />
      <FormControl
        ref={inputRef}
        type={field.type}
        name={field.name}
        value={information ? information[field.name] : ''}
        placeholder={field.placeholder ? t(field.placeholder) : ''}
        isInvalid={validation[field.name]}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        readOnly={field?.isReadOnly}
        $isTextColorDisabled={isTextColorDisabled}
      />
      <FormControlFeedback vale={t(validation[field.name])} />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 32px;
  position: relative;
  ${Fade};

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    margin-bottom: 34px;
  }
`;

const FormControl = styled(Form.Control)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;

  color: ${(props) =>
    props.$isTextColorDisabled
      ? 'var(--ds-c-grey-neutral)'
      : 'var(--ds-c-grey-dark)'};
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 12px;
  padding-inline: 10px;
  background: ${(props) =>
    props.$isTextColorDisabled
      ? 'var(--ds-c-grey-disabled)'
      : 'var(--ds-c-white)'};
  cursor: ${(props) => props.$isTextColorDisabled && 'not-allowed'};

  &:focus {
    background: ${(props) =>
      props.$isTextColorDisabled && 'var(--ds-c-grey-disabled)!important'};
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-inline: 10px !important;
  }

  &[readonly] {
    background: ${(props) =>
      props.$isTextColorDisabled && 'var(--ds-c-grey-disabled)!important'};
    border: none !important;
  }

  &::placeholder {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    text-transform: none;

    color: var(--ds-c-blue-disabled) !important;

    @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
      font-size: 16px;
      line-height: 20px;
    }
  }

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

export default FormInputValidation;
